import { addMinutes, isValid } from 'date-fns';
import { resolveSlotConfig } from '../../../components/CustomBlocks/slotBooking/slotTypes';
import { formatSlotSummary } from '../../../components/CustomBlocks/slotBooking/slotUtils';
import { DEFAULT_FIELD_NAMES } from '../../../components/form-builder/newFieldDefaults';
import { FormResponse } from '../useFormResponses';

const DEFAULT_DURATION_MINUTES = 30;
const BOOKING_KEY_RE = new RegExp(`^${DEFAULT_FIELD_NAMES.SlotBooking}(_\\d+)?$`);

export const formatBookingValue = (key: string, value: unknown): string | null => {
  if (!BOOKING_KEY_RE.test(key) || typeof value !== 'string' || !isValid(new Date(value))) {
    return null;
  }
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${formatSlotSummary(value, timezone, false)} (${timezone.replace(/_/g, ' ')})`;
};
const TITLE_KEYS = ['name', 'full_name', 'fullName', 'email', 'phone'];

export interface SlotField {
  fieldName: string;
  label: string;
  durationMinutes: number;
}

export interface BookingEvent {
  title: string;
  start: Date;
  end: Date;
  responseId: string;
  fieldLabel: string;
}

const toFieldList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.task_data)) return data.task_data;
  if (Array.isArray(data.data)) return data.data;
  return Object.keys(data)
    .filter((key) => !isNaN(Number(key)))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => data[key]);
};

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '').trim();

export const getSlotFields = (formData: any): SlotField[] =>
  toFieldList(formData)
    .filter((field) => field?.key === 'SlotBooking' && field.field_name)
    .map((field) => {
      const config = resolveSlotConfig(field.slotConfig);
      return {
        fieldName: field.field_name,
        label: stripHtml(field.label || '') || 'Booking',
        durationMinutes:
          config.source === 'custom'
            ? Number(config.custom.durationMinutes) || DEFAULT_DURATION_MINUTES
            : DEFAULT_DURATION_MINUTES,
      };
    });

const getBookerName = (data: Record<string, any>): string => {
  const key = TITLE_KEYS.find((k) => typeof data?.[k] === 'string' && data[k].trim());
  return key ? data[key].trim() : '';
};

export const toBookingEvents = (
  responses: FormResponse[],
  slotFields: SlotField[],
): BookingEvent[] =>
  responses.flatMap((response) => {
    const responseId = response.id || response._id || '';
    if (!responseId) return [];
    const booker = getBookerName(response.data);

    return slotFields.flatMap((field) => {
      const raw = response.data?.[field.fieldName];
      const start = raw ? new Date(raw) : null;
      if (!start || !isValid(start)) return [];
      return [
        {
          title: booker || field.label,
          start,
          end: addMinutes(start, field.durationMinutes),
          responseId,
          fieldLabel: field.label,
        },
      ];
    });
  });
